import { COOKIES } from "@/constants";
import { isTokenExpired } from "@/helpers/jwt.helpers";
import { getCookie, setCookie } from "@/utils/cookies";
import { notifyCookieChanged } from "@/utils/cookieEvents";
import { auth } from "@/config/firebase";
import { getIdToken, type User } from "firebase/auth";
import { verifySharedToken } from "./authApi";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { isLocal } from "@/helpers/cookie.helper";
import { handleApiError, shouldHandleGlobally } from "@/utils/errorHandler";

// Opt individual requests out of the global error toast (honored by the response
// interceptor below). Declared here so callers can pass it in a typed request
// config — used by optional feature reads (dashboards/use-cases) that 404 on
// older backends and degrade silently instead of surfacing a raw toast.
//   `true`      → suppress the toast for every status.
//   `number[]`  → suppress only for those HTTP statuses (e.g. `[404]`), so a
//                 genuine 500 still surfaces globally.
declare module "axios" {
  export interface AxiosRequestConfig {
    skipGlobalErrorHandler?: boolean | number[];
  }
}

export interface IResponse<T> extends AxiosResponse<T> {}
export interface IError extends AxiosError<IErrorData> {}
export interface IErrorData {
  errors: {
    code: string;
    message: string;
  };
}

// Define base API configuration
const apiConfig: AxiosRequestConfig = {
  baseURL: "",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 360000, // 6 minutes
};

// Create an axios instance with the config
const apiClient: AxiosInstance = axios.create(apiConfig);

/**
 * Is the signed-in session that started a refresh still the live one?
 *
 * Checked again at WRITE time, not just before the exchange starts: the exchange
 * is two network round trips, and a logout landing inside that window would
 * otherwise have its freshly deleted cookie written straight back. The uid
 * comparison also covers an account switch mid-exchange, where the token that
 * comes back belongs to the previous user.
 */
const isSessionStillLive = (startedFor: User): boolean =>
  !!getCookie(COOKIES.saras_global) && auth.currentUser?.uid === startedFor.uid;

const writeAuthTokenCookie = (token: string) => {
  setCookie(COOKIES.AUTH_TOKEN, token, {
    path: "/",
    domain: isLocal ? "localhost" : window.location.hostname,
    secure: true,
    sameSite: "strict",
  });
  // This write happens outside React, so nothing re-reads the cookie on its own.
  // `useCookie` holds the value in state and `useRbac` is gated on it
  // (`enabled: !!currentUser && !!authTokenCookie`), so without this announcement a
  // session recovered here never fetches roles and the app renders "Access
  // restricted" despite having a perfectly good token.
  notifyCookieChanged(COOKIES.AUTH_TOKEN);
};

/**
 * Refresh the AUTH_TOKEN cookie — whether it expired or was never written (the
 * post-login boot race, where `currentUser` is live several round trips before
 * the cookie lands).
 *
 * A raw Firebase ID token is NOT a saras token — the IQ backend reads a numeric
 * `userId` claim the Firebase token doesn't carry, so writing the Firebase token
 * to the cookie makes every eager boot call 401 until a manual reload. So we do
 * the same exchange the session-restore flow does: fresh Firebase ID token →
 * `verify-firebase-token` → saras JWT.
 *
 * If the exchange itself fails (user-service down), fall back to the Firebase ID
 * token — i.e. exactly the old behavior. This must never be stricter than before.
 */
const refreshAuthToken = async (currentUser: User): Promise<string | null> => {
  // Force-refresh the Firebase ID token first; a failure here is fatal as before.
  const firebaseIdToken = await getIdToken(currentUser, true);
  let nextToken = firebaseIdToken;
  try {
    const data = await verifySharedToken(firebaseIdToken);
    if (data?.token) {
      nextToken = data.token;
    } else {
      console.error("Token exchange returned no saras token; using the Firebase ID token");
    }
  } catch (error) {
    console.error("Error exchanging the Firebase token for a saras token:", error);
  }
  // The session can die WHILE the exchange is in flight. Writing here would undo
  // a deliberate sign-out, so drop the token instead — the caller then sends the
  // request unauthenticated and the app is already on its way to /auth.
  if (!isSessionStillLive(currentUser)) {
    return null;
  }
  writeAuthTokenCookie(nextToken);
  return nextToken;
};

// Single-flight guard: N concurrent requests that all see the same expired cookie
// must trigger ONE refresh + exchange and share its result.
let refreshInFlight: Promise<string | null> | null = null;

const refreshAuthTokenOnce = (currentUser: User): Promise<string | null> => {
  if (!refreshInFlight) {
    refreshInFlight = refreshAuthToken(currentUser).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// Request interceptor
apiClient.interceptors.request.use(
  async (config) => {
    // `headers.skip` marks the unauthenticated exchange endpoints
    // (`verify-firebase-token`, `firebase-custom-token`). They carry no auth by
    // design, and `refreshAuthToken` posts through this very instance — leaving
    // them untouched is what keeps the interceptor from re-entering itself.
    if (config.headers?.skip) {
      return config;
    }
    // A MISSING cookie is recovered the same way an expired one is. Right after
    // login the cookie doesn't exist yet: `useUserAuth` writes it at the very end
    // of its chain (verify-firebase-token -> firebase-custom-token ->
    // signInWithCustomToken -> getIdToken -> updateAuthTokenCookie), while
    // `currentUser` flips much earlier off Firebase's persisted session. Every
    // query gated on `currentUser` therefore fires ~3 round trips ahead of the
    // token, and without this the request went out with NO Authorization header
    // at all — the backend's `HTTPBearer` answers a missing header with 403.
    // `refreshAuthTokenOnce` is single-flight, so the whole boot burst waits on
    // one exchange.
    let token = getCookie(COOKIES.AUTH_TOKEN);
    // ...but ONLY while the shared `saras_global` session cookie is still there.
    // Logout deletes both cookies synchronously and only then signs out of
    // Firebase, so for a moment `auth.currentUser` is still set with no auth
    // cookie. Without this gate a request racing that window would refresh and
    // write the token cookie straight back after a deliberate sign-out. The gate
    // costs the boot fix nothing: the accounts app sets `saras_global` before it
    // redirects here, and `useUserAuth` only starts the exchange once it exists,
    // so it is present for the whole post-login race. `refreshAuthToken` checks
    // the same thing again before it writes, for a logout that lands mid-flight.
    const canRecoverMissingToken = !!getCookie(COOKIES.saras_global);
    // `isTokenExpired` decodes the JWT and throws on null/undefined, so the
    // missing case must never reach it.
    if (token ? isTokenExpired(token) : canRecoverMissingToken) {
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          // `null` = the session died mid-exchange; fall through with no token.
          token = await refreshAuthTokenOnce(currentUser);
        } catch (error) {
          console.error("Error refreshing token:", error);
          throw new Error("Failed to refresh authentication token");
        }
      } else if (token) {
        // Only an EXPIRED cookie with no signed-in user is fatal, as before. A
        // missing cookie with no user is the genuine pre-auth case (boot before
        // sign-in, shared/public routes) — it must still go out unauthenticated
        // rather than start throwing.
        throw new Error("No authenticated user found");
      }
    }
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // No SarasAdmin "View as user" (doAs) rule here, on purpose. That header is
    // opted into at the call site by the few dashboard data reads that want it
    // (see `services/viewAsUser`). An interceptor can only judge a request by its
    // method and URL, and neither is enough: a mutation can share a URL with a
    // read, and an id-shaped pattern silently stops matching when ids change
    // shape. So every request leaves here under the admin's own identity.
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Skip global handler when the originating request opted out. `true` skips
    // every status; an array skips only the listed HTTP statuses (so, e.g., a
    // `[404]` opt-out still surfaces a real 500).
    const skipConfig = (error.config as AxiosRequestConfig | undefined)?.skipGlobalErrorHandler;
    const skipGlobal = Array.isArray(skipConfig)
      ? error.response != null && skipConfig.includes(error.response.status)
      : Boolean(skipConfig);

    // Check if this error should be handled globally
    if (!skipGlobal && shouldHandleGlobally(error)) {
      // Handle the error and show toast notification
      handleApiError(error, {
        showToast: true,
      });
    }

    // Handle 401 Unauthorized responses
    // if (error.response?.status === 401) {
    //   // Clear auth state and redirect to login
    //   localStorage.removeItem("token");
    //   window.location.href = "/auth";
    // }
    
    return Promise.reject(error);
  }
);

// Generic GET request
export const get = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  if (config?.headers?.skip || false) {
    config.headers = {
      ...(config.headers || {}),
    };
  }
  const response = await apiClient.get<T>(url, config);
  return response.data;
};

// Generic POST request
export const post = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
};

// Generic PUT request
export const put = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  const response = await apiClient.put<T>(url, data, config);
  return response.data;
};

// Generic PATCH request
export const patch = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  const response = await apiClient.patch<T>(url, data, config);
  return response.data;
};

// Generic DELETE request
export const del = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
};

// Export the axios instance as well
export default apiClient;
